import _defineProperty from "@babel/runtime/helpers/defineProperty";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import { isNil } from '@flowio/is';
import { getApiClient, getApiInternalClient } from '../../services';
import { isInlineAuthorizationDetails, isOnlineAuthorization, isAuthorizationSuccessful } from '../../utilities/checks';
import snakeCaseToCamelCase from '../../utilities/snake-case-to-camel-case';
var APPLEPAY_SUPPORTED_VERSION = 4;
export var UnavailabilityReasons = {
  VERSION_NOT_SUPPORTED: 'VERSION_NOT_SUPPORTED',
  NOT_SUPPORTED: 'NOT_SUPPORTED',
  NOT_CONFIGURED: 'NOT_CONFIGURED'
};
export function openPaymentSetup(_ref) {
  var merchantId = _ref.merchantId;

  if ('ApplePaySession' in window) {
    return ApplePaySession.openPaymentSetup(merchantId);
  }

  return Promise.resolve(false);
}
export function getAvailability() {
  var availability;
  return new Promise(function (resolve) {
    if ('ApplePaySession' in window) {
      var canMakePayments = ApplePaySession.canMakePayments();

      if (!ApplePaySession.supportsVersion(APPLEPAY_SUPPORTED_VERSION)) {
        availability = {
          available: false,
          reason: UnavailabilityReasons.VERSION_NOT_SUPPORTED
        };
      } else if (!canMakePayments) {
        availability = {
          available: false,
          reason: UnavailabilityReasons.NOT_CONFIGURED
        };
      } else {
        availability = {
          available: true
        };
      }
    } else {
      availability = {
        available: false,
        reason: UnavailabilityReasons.NOT_SUPPORTED
      };
    }

    resolve(availability);
  });
}

function mapFlowParametersToApplePayParams(info) {
  var _info$shipping_method, _info$line_items;

  return {
    countryCode: info.country_code,
    currencyCode: info.currency_code,
    merchantCapabilities: info.merchant_capabilities,
    supportedNetworks: info.supported_networks,
    shippingContact: info.shipping_contact,
    shippingMethods: (_info$shipping_method = info.shipping_methods) === null || _info$shipping_method === void 0 ? void 0 : _info$shipping_method.map(function (sm) {
      return _objectSpread(_objectSpread({}, sm), {}, {
        amount: String(sm.amount)
      });
    }),
    billingContact: info.billing_contact,
    lineItems: (_info$line_items = info.line_items) === null || _info$line_items === void 0 ? void 0 : _info$line_items.map(function (sm) {
      return _objectSpread(_objectSpread({}, sm), {}, {
        amount: String(sm.amount)
      });
    }),
    shippingType: snakeCaseToCamelCase(info.shipping_type)
  };
}

export function createApplePaySession(info, params) {
  return new Promise(function (resolve, reject) {
    var session = new ApplePaySession(APPLEPAY_SUPPORTED_VERSION, _objectSpread(_objectSpread({}, mapFlowParametersToApplePayParams(info)), {}, {
      total: {
        amount: String(info.total.amount),
        label: info.total.label
      }
    }));
    var authKey;

    session.onvalidatemerchant = function (event) {
      getApiClient().authorizations.post({
        headers: {
          authorization: "Session ".concat(params.sessionId)
        },
        organization: params.organization,
        body: {
          discriminator: 'inline_authorization_form',
          amount: info.total.amount,
          currency: info.currency_code,
          method: 'apple_pay',
          order_number: params.orderNumber,
          payload: {
            parameters: {
              discriminator: 'apple_pay_merchant_validation_payload',
              validation_url: event.validationURL
            }
          }
        }
      }).then(function (response) {
        if (response.ok) {
          var opaqueSession = isOnlineAuthorization(response.body) && isInlineAuthorizationDetails(response.body.details) && response.body.details.parameters;
          authKey = response.body.key;

          if (isNil(opaqueSession)) {
            reject(new Error('ApplePay opaque session is nil.'));
            return;
          }

          session.completeMerchantValidation(opaqueSession);
          return;
        }

        session.abort();
        resolve({
          status: 'error',
          reason: 'Authorization creation failure'
        });
      }).catch(function (e) {
        session.abort();
        resolve({
          status: 'error',
          reason: "Apple Pay Authorization creation failed. ".concat(e)
        });
      });
    };

    session.onpaymentauthorized = function (event) {
      getApiInternalClient().authorizations.putPayloadByKey({
        headers: {
          authorization: "Session ".concat(params.sessionId)
        },
        key: authKey,
        organization: params.organization,
        body: {
          discriminator: 'apple_pay_authorization_payload',
          apple_pay_token: JSON.stringify(event.payment.token.paymentData)
        }
      }).then(function (response) {
        if (response.ok && isOnlineAuthorization(response.body) && isAuthorizationSuccessful(response.body)) {
          resolve({
            status: 'success',
            authorization: {
              key: response.body.key,
              status: response.body.result.status
            }
          });
          session.completePayment({
            status: ApplePaySession.STATUS_SUCCESS
          });
          return;
        }

        session.completePayment({
          status: ApplePaySession.STATUS_FAILURE,
          errors: [{
            code: 'unknown',
            message: 'Could not process your Apple Pay payment.'
          }]
        });
        resolve({
          status: 'failure',
          reason: "Put authorization details request failed. (".concat(response.status, ")")
        });
      }).catch(function () {
        resolve({
          status: 'error',
          reason: 'Error occurred making put authorization details request'
        });
        session.completePayment({
          status: ApplePaySession.STATUS_FAILURE,
          errors: [{
            code: 'unknown',
            message: 'Could not process your Apple Pay payment.'
          }]
        });
      });
    };

    session.oncancel = function () {
      resolve({
        status: 'failure',
        reason: 'User cancelled'
      });
    };

    session.begin();
  });
}
//# sourceMappingURL=core.js.map


//////////////////
// WEBPACK FOOTER
// ./~/@flowio/payment/lib/esm/payment_methods/applepay/core.js
// module id = 592
// module chunks = 0 1